import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import Button from 'flarum/common/components/Button';
import normalize from '../utils/normalize';
import editPostButton from '../utils/editPostButton';
const PAGE_LIMIT = 50;
const VISIBLE_STEP = 30;
export const GLOSSARY_TAG_SLUG = 'glossaire';

/**
 * /glossary - every discussion tagged "Glossaire", one per mycology term
 * (title = term, first post = definition, written by Christian Frund) -
 * matching champis.net/glossary. Sorted alphabetically (the reference
 * site's own order is *almost* alphabetical already, with a handful of
 * terms tacked on at the end out of sequence - not worth reproducing that
 * quirk here).
 *
 * Same "load everything up front, VISIBLE_STEP only gates what's
 * rendered" approach as /descriptions, /keys and /paintings.
 *
 * An A-Z row above the list (see alphabet()) lets a reader jump straight
 * to one letter - `rows` in view() applies that filter on top of the same
 * already-loaded, already-sorted `this.discussions`, so picking a letter
 * is instant, no extra request.
 */
export default class GlossaryPage extends Page {
  discussions = null;
  error = false;
  visibleCount = (() => VISIBLE_STEP)();
  selectedLetter = null;
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(app.translator.trans('champis-others.forum.glossary_page.title'));
    this.discussions = null;
    this.error = false;
    this.visibleCount = VISIBLE_STEP;
    this.selectedLetter = null;
    this.loadAll();
  }
  selectLetter(letter) {
    // Clicking the already-selected letter again clears it back to the
    // full list, same "toggle off" convention as core's own active sort
    // options.
    this.selectedLetter = this.selectedLetter === letter ? null : letter;
    this.visibleCount = VISIBLE_STEP;
  }

  // Only letters an actual term starts with are worth showing as
  // clickable - of the 26, this glossary currently only ever covers
  // around 16 (nothing starting with "Q" or "X", for instance), and a
  // button that always leads to an empty list is worse than no button.
  // Derived from the loaded discussions themselves rather than hardcoded,
  // so it stays correct as terms are added or removed.
  availableLetters() {
    const letters = new Set((this.discussions || []).map(d => normalize(d.title()).charAt(0).toUpperCase()));
    return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').filter(letter => letters.has(letter));
  }
  loadAll() {
    this.discussions = null;
    this.error = false;
    const url = app.forum.attribute('apiUrl') + '/discussions';
    const pageParams = offset => ({
      filter: {
        tag: GLOSSARY_TAG_SLUG
      },
      page: {
        limit: PAGE_LIMIT,
        offset
      },
      include: 'firstPost'
    });

    // Once the first page reports the real total (`meta.page.total` - a
    // plain field, unlike `links.next`, which comes back missing the
    // `/api` prefix in this environment and 404s), every remaining page is
    // requested *concurrently* rather than one after another: wall-clock
    // cost becomes the slowest single page rather than the sum of all of
    // them. Sorting only once everything is back also avoids re-sorting
    // (and visibly reshuffling on screen) after every page the way
    // fetching them one at a time did before - same fix as /descriptions,
    // /keys and /paintings.
    app.request({
      method: 'GET',
      url,
      params: pageParams(0)
    }).then(firstPayload => {
      const total = firstPayload.meta?.page?.total ?? 0;
      const firstPage = app.store.pushPayload(firstPayload);
      const remainingOffsets = [];
      for (let offset = PAGE_LIMIT; offset < total; offset += PAGE_LIMIT) {
        remainingOffsets.push(offset);
      }
      Promise.all(remainingOffsets.map(offset => app.request({
        method: 'GET',
        url,
        params: pageParams(offset)
      }))).then(remainingPayloads => {
        const all = remainingPayloads.reduce((acc, payload) => acc.concat(app.store.pushPayload(payload)), firstPage);
        this.discussions = all.slice().sort((a, b) => normalize(a.title()).localeCompare(normalize(b.title())));
        m.redraw();
      }).catch(() => {
        this.error = true;
        m.redraw();
      });
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  view() {
    const all = this.discussions || [];
    const rows = this.selectedLetter ? all.filter(d => normalize(d.title()).charAt(0).toUpperCase() === this.selectedLetter) : all;
    const visible = rows.slice(0, this.visibleCount);
    return m("div", {
      className: "Page GlossaryPage"
    }, m("div", {
      className: "container"
    }, m("h1", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-book",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-others.forum.glossary_page.title')), this.discussions && this.discussions.length > 0 && this.alphabet(), m("dl", {
      className: "ChampisGlossaryList"
    }, !this.discussions && this.error ? m(Placeholder, {
      text: app.translator.trans('champis-others.forum.glossary_page.error')
    }) : !this.discussions ? m(LoadingIndicator, null) : rows.length === 0 ? m(Placeholder, {
      text: app.translator.trans('champis-others.forum.glossary_page.empty')
    }) : visible.map(discussion => this.entry(discussion))), visible.length < rows.length && m("div", {
      className: "ChampisGlossaryList-loadMore"
    }, m(Button, {
      className: "Button",
      onclick: () => this.visibleCount += VISIBLE_STEP
    }, app.translator.trans('champis-others.forum.glossary_page.load_more_button')))));
  }

  // A-Z jump list above the term list itself - clicking a letter filters
  // down to just the terms starting with it (see view()'s own `rows`),
  // clicking it again (or "Tout") clears back to the full list. Letters
  // with no term at all render disabled rather than being left out
  // entirely, so the row stays a stable, predictable A-through-Z shape
  // instead of reflowing around whichever letters happen to be missing.
  alphabet() {
    const available = this.availableLetters();
    return m("div", {
      className: "ChampisGlossaryList-alphabet"
    }, m("a", {
      className: 'ChampisGlossaryList-letter' + (this.selectedLetter ? '' : ' active'),
      onclick: () => this.selectLetter(null)
    }, app.translator.trans('champis-others.forum.glossary_page.all_letters')), 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => {
      const has = available.includes(letter);
      return m("a", {
        key: letter,
        className: 'ChampisGlossaryList-letter' + (this.selectedLetter === letter ? ' active' : '') + (has ? '' : ' disabled'),
        onclick: () => has && this.selectLetter(letter)
      }, letter);
    }));
  }
  entry(discussion) {
    const post = discussion.firstPost();
    return m("div", {
      key: discussion.id(),
      className: "ChampisGlossaryList-entry"
    }, m("dt", {
      className: "ChampisGlossaryList-term"
    }, discussion.title(), " ", editPostButton(post)), m("dd", {
      className: "ChampisGlossaryList-definition"
    }, post ? m.trust(post.contentHtml() || '') : m(LoadingIndicator, {
      display: "block",
      size: "small"
    })));
  }
}
flarum.reg.add('champis-others', 'forum/components/GlossaryPage', GlossaryPage);